import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { billingApi, billingQueryKeys } from "../../shared/api/billing";
import { apiError } from "../../shared/api/client";
import {
  useRowSelection,
  BulkDeleteBar,
  runBulkDelete,
} from "../../shared/ui/bulkSelect";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import {
  formatDate,
  formatPhone,
  formatTime,
  formatVndWithUnit,
  invoiceStatusLabel,
  isoDate,
  shiftIsoDate,
} from "../../shared/format";
import type {
  InvoiceListFilter,
  InvoiceStatus,
  InvoiceSummary,
} from "../../shared/types/billing";
import { PageHeader } from "../../shared/ui/PageHeader";
import { DateRangePicker } from "../../shared/ui/DateRangePicker";
import { ErrorState } from "../../shared/ui/ErrorState";
import { PatientLink } from "../../shared/ui/PatientLink";
import { Toolbar } from "../../shared/ui/Toolbar";
import { DataTable, type Column } from "../../shared/ui/DataTable";
import { Pagination } from "../../shared/ui/Pagination";
import { InvoiceStatusBadge } from "./components/InvoiceStatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: InvoiceStatus | ""; label: string }[] = [
  { value: "", label: "Tất cả trạng thái" },
  { value: "DRAFT", label: invoiceStatusLabel.DRAFT },
  { value: "POSTED", label: invoiceStatusLabel.POSTED },
  { value: "PARTIALLY_PAID", label: invoiceStatusLabel.PARTIALLY_PAID },
  { value: "PAID", label: invoiceStatusLabel.PAID },
  { value: "VOIDED", label: invoiceStatusLabel.VOIDED },
];

// shadcn Select cannot use an empty-string item value; map "" ↔ sentinel.
const ALL_STATUS = "__ALL__";

export function InvoiceListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const sel = useRowSelection();
  const [error, setError] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);
  // Bộ lọc nằm trên URL; mặc định 7 ngày gần nhất (không ghi lên URL khi chưa đổi).
  const { filters, set } = useUrlFilters({
    q: param.string(),
    status: param.oneOf(STATUS_OPTIONS.map((o) => o.value).filter(Boolean) as InvoiceStatus[]),
    from: param.isoDate(() => shiftIsoDate(new Date(), -7)),
    to: param.isoDate(() => isoDate(new Date())),
    page: param.int(),
  });
  const { q, status, from: fromDate, to: toDate, page } = filters;
  const debouncedQ = useDebounce(q, 250);

  const filter: InvoiceListFilter = useMemo(
    () => ({
      q: debouncedQ || null,
      status: status || null,
      fromDate: fromDate || null,
      toDate: toDate || null,
    }),
    [debouncedQ, status, fromDate, toDate]
  );

  const list = useQuery({
    queryKey: billingQueryKeys.invoiceList(filter, page),
    queryFn: () => billingApi.list(filter, { page, size: PAGE_SIZE, sort: "issuedAt,desc" }),
    placeholderData: (prev) => prev,
  });

  const totals = useQuery({
    queryKey: billingQueryKeys.invoiceTotals(filter),
    queryFn: () => billingApi.totalsByStatus(filter),
  });

  const totalsByStatus = useMemo(() => {
    const map = new Map<InvoiceStatus, { count: number; amount: number }>();
    for (const t of totals.data ?? []) {
      map.set(t.status, { count: t.count, amount: t.amount });
    }
    return map;
  }, [totals.data]);

  // Ô "Tổng" KHÔNG cộng hoá đơn đã huỷ (#19845): huỷ là chưa từng là một khoản phải thu, và nó
  // đã có ô riêng. Nháp vẫn được cộng — nhãn nói rõ cả hai.
  const valid = (totals.data ?? []).filter((t) => t.status !== "VOIDED");
  const grandTotalAll = valid.reduce((s, t) => s + t.amount, 0);
  const grandCountAll = valid.reduce((s, t) => s + t.count, 0);
  // Đang tải / lỗi thì KHÔNG hiện 0: một ô "0" trông như "không có hoá đơn nào".
  const kpi = (v: string) => (totals.isLoading ? "…" : totals.isError ? "—" : v);

  const columns: Column<InvoiceSummary>[] = [
    {
      key: "code",
      header: "Số HĐ",
      width: 170,
      render: (inv) => (
        <Link
          to={`/invoices/${inv.id}`}
          className="font-mono font-semibold tabular-nums text-primary hover:underline"
        >
          {inv.code}
        </Link>
      ),
    },
    {
      key: "when",
      header: "Thời gian",
      width: 130,
      render: (inv) => (
        <div>
          <div className="font-semibold">{formatTime(inv.issuedAt)}</div>
          <div className="font-mono tabular-nums text-muted-foreground">
            {formatDate(inv.issuedAt)}
          </div>
        </div>
      ),
    },
    {
      key: "patient",
      header: "Bệnh nhân",
      render: (inv) => (
        <div>
          <PatientLink
            id={inv.patientId}
            name={inv.patientName}
            className="font-semibold"
          />
          {inv.patientPhone && (
            <div className="font-mono tabular-nums text-muted-foreground">
              {formatPhone(inv.patientPhone)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "doctor",
      header: "Bác sĩ",
      render: (inv) => inv.doctorName ?? "—",
    },
    {
      key: "items",
      header: "Số mục",
      width: 80,
      numeric: true,
      render: (inv) => inv.itemCount,
    },
    {
      key: "grand",
      header: "Tổng",
      width: 140,
      numeric: true,
      render: (inv) => (
        <strong className="tabular-nums">{formatVndWithUnit(inv.grandTotal)}</strong>
      ),
    },
    {
      key: "paid",
      header: "Đã thu",
      width: 130,
      numeric: true,
      render: (inv) => (
        <span
          className={
            inv.paidTotal > 0
              ? "tabular-nums text-emerald-600"
              : "tabular-nums"
          }
        >
          {formatVndWithUnit(inv.paidTotal)}
        </span>
      ),
    },
    {
      key: "balance",
      header: "Còn lại",
      width: 130,
      numeric: true,
      render: (inv) => (
        <span
          className={
            inv.balanceDue > 0
              ? "tabular-nums text-amber-600"
              : "tabular-nums"
          }
        >
          {formatVndWithUnit(inv.balanceDue)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Trạng thái",
      width: 150,
      render: (inv) => <InvoiceStatusBadge status={inv.status} />,
    },
    {
      key: "actions",
      header: "",
      ariaLabel: "Thao tác",
      width: 90,
      stickyRight: true,
      render: (inv) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          title="Xoá vĩnh viễn hoá đơn"
          aria-label="Xoá hoá đơn"
          onClick={(e) => {
            e.stopPropagation();
            void removeOne(inv.id);
          }}
        >
          <Trash2 />
        </Button>
      ),
    },
  ];

  async function removeOne(id: string) {
    if (!confirm("Xoá vĩnh viễn hoá đơn này? Không thể hoàn tác.")) return;
    try {
      await billingApi.remove(id);
      setError(null);
      qc.invalidateQueries({ queryKey: billingQueryKeys.invoiceList(filter, page) });
    } catch (e) {
      setError(apiError(e).detail);
    }
  }

  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete([...sel.selectedIds], billingApi.remove);
    setBulkPending(false);
    sel.clear();
    qc.invalidateQueries({ queryKey: billingQueryKeys.invoiceList(filter, page) });
    setError(failed.length ? `${failed.length} hoá đơn không xoá được.` : null);
  }

  const data = list.data?.content ?? [];

  return (
    <div>
      <PageHeader
        title="Hoá đơn"
        description="Tất cả hoá đơn và thanh toán của phòng khám."
        actions={
          <>
            <Button asChild variant="secondary">
              <Link to="/payments">Sổ thanh toán</Link>
            </Button>
            <Button asChild>
              <Link to="/invoices/new">
                <Plus />
                Tạo hoá đơn
              </Link>
            </Button>
          </>
        }
      />

      {totals.isError && (
        <div className="mb-3">
          <ErrorState
            inline
            error={totals.error}
            title="Không tải được số tổng theo bộ lọc"
            onRetry={() => totals.refetch()}
          />
        </div>
      )}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiTile
          tone="primary"
          label="Tổng hoá đơn (theo bộ lọc, không gồm đã huỷ)"
          value={kpi(String(grandCountAll))}
          sublabel={kpi(formatVndWithUnit(grandTotalAll))}
        />
        <KpiTile
          tone="success"
          label={invoiceStatusLabel.PAID}
          value={kpi(String(totalsByStatus.get("PAID")?.count ?? 0))}
          sublabel={kpi(formatVndWithUnit(totalsByStatus.get("PAID")?.amount ?? 0))}
        />
        <KpiTile
          tone="warning"
          label={`${invoiceStatusLabel.POSTED} / ${invoiceStatusLabel.PARTIALLY_PAID}`}
          value={kpi(
            String(
              (totalsByStatus.get("POSTED")?.count ?? 0) +
                (totalsByStatus.get("PARTIALLY_PAID")?.count ?? 0)
            )
          )}
          sublabel={kpi(
            formatVndWithUnit(
              (totalsByStatus.get("POSTED")?.amount ?? 0) +
                (totalsByStatus.get("PARTIALLY_PAID")?.amount ?? 0)
            )
          )}
        />
        <KpiTile
          tone="neutral"
          label={invoiceStatusLabel.DRAFT}
          value={kpi(String(totalsByStatus.get("DRAFT")?.count ?? 0))}
          sublabel={kpi(formatVndWithUnit(totalsByStatus.get("DRAFT")?.amount ?? 0))}
        />
        <KpiTile
          tone="danger"
          label={invoiceStatusLabel.VOIDED}
          value={kpi(String(totalsByStatus.get("VOIDED")?.count ?? 0))}
          sublabel={kpi(formatVndWithUnit(totalsByStatus.get("VOIDED")?.amount ?? 0))}
        />
      </div>

      <Toolbar
        search={{
          value: q,
          onChange: (v) => set({ q: v }),
          placeholder: "Tìm theo số HĐ, tên/SĐT bệnh nhân, bác sĩ…",
        }}
        filters={
          <>
            <Select
              value={status === "" ? ALL_STATUS : status}
              onValueChange={(v) =>
                set({ status: v === ALL_STATUS ? "" : (v as InvoiceStatus) })
              }
            >
              <SelectTrigger className="w-[180px]" aria-label="Trạng thái">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value === "" ? ALL_STATUS : o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangePicker
              id="filter-from"
              className="w-[260px]"
              ariaLabel="Khoảng ngày hoá đơn"
              from={fromDate}
              to={toDate}
              onChange={({ from, to }) => set({ from, to })}
            />
          </>
        }
      />

      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

      <BulkDeleteBar
        count={sel.count}
        noun="hoá đơn"
        pending={bulkPending}
        onClear={sel.clear}
        onDelete={bulkDelete}
      />

      <DataTable
        ariaLabel="Danh sách hoá đơn"
        columns={columns}
        data={data}
        loading={list.isLoading}
        error={list.error}
        rowKey={(row) => row.id}
        selectedIds={sel.selectedIds}
        onSelectionChange={sel.setSelectedIds}
        onRowClick={(row) => navigate(`/invoices/${row.id}`)}
        empty={{
          title: "Chưa có hoá đơn",
          description: "Tạo hoá đơn mới từ phiếu khám hoặc trực tiếp.",
          actions: (
            <Button asChild>
              <Link to="/invoices/new">
                <Plus />
                Tạo hoá đơn
              </Link>
            </Button>
          ),
        }}
        footer={
          list.data && list.data.totalElements > PAGE_SIZE ? (
            <Pagination
              page={list.data.number}
              totalPages={list.data.totalPages}
              totalElements={list.data.totalElements}
              pageSize={list.data.size}
              onChange={(p) => set({ page: p })}
            />
          ) : null
        }
      />
    </div>
  );
}

const KPI_TONE: Record<
  "primary" | "success" | "warning" | "danger" | "neutral",
  string
> = {
  primary: "border-l-primary",
  success: "border-l-emerald-500",
  warning: "border-l-amber-500",
  danger: "border-l-destructive",
  neutral: "border-l-border",
};

function KpiTile({
  tone,
  label,
  value,
  sublabel,
}: {
  tone: "primary" | "success" | "warning" | "danger" | "neutral";
  label: string;
  value: string;
  sublabel?: string;
}) {
  return (
    <Card className={`border-l-4 ${KPI_TONE[tone]}`}>
      <CardContent className="p-4">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
          {value}
        </div>
        {sublabel && (
          <div className="mt-0.5 text-sm tabular-nums text-muted-foreground">
            {sublabel}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
